/**
 * Email Alerts Management Page
 * Allows users to view, create, edit, and delete email alerts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Frequency labels
const FREQUENCY_LABELS = {
  immediate: 'Immediate',
  daily: 'Daily',
  weekly: 'Weekly',
};

// Search type labels
const SEARCH_TYPE_LABELS = {
  parliamentarians: 'Parliamentarians',
  hansard: 'Hansard',
  bills: 'Bills',
  committees: 'Committees',
  qon: 'Questions on Notice',
  qiw: 'Questions in Writing',
  tableddocs: 'Tabled Documents',
  electorates: 'Electorates',
  parties: 'Parties',
  ministries: 'Ministries',
  divisions: 'Divisions',
  senate_divisions: 'Senate Divisions',
  motions: 'Motions',
  petitions: 'Petitions',
  committee_reports: 'Committee Reports',
  global: 'Global Search',
};

function AlertsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [creating, setCreating] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editAlert, setEditAlert] = useState(null);
  const [editFrequency, setEditFrequency] = useState('daily');
  const [editScheduleTime, setEditScheduleTime] = useState('09:00');
  const [editSaving, setEditSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // History dialog state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAlert, setHistoryAlert] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/alerts');
      setAlerts(response.data.alerts || []);
      setError('');
    } catch (err) {
      console.error('Error fetching alerts:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view your alerts');
      } else {
        setError('Failed to load alerts');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSavedSearches = useCallback(async () => {
    try {
      const response = await api.get('/saved-searches');
      setSavedSearches(response.data.searches || []);
    } catch (err) {
      console.error('Error fetching saved searches:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchAlerts();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, fetchAlerts]);

  // Create alert handlers
  const handleCreateOpen = () => {
    fetchSavedSearches();
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setSelectedSearch('');
    setFrequency('daily');
    setScheduleTime('09:00');
  };

  const handleCreate = async () => {
    if (!selectedSearch) return;

    setCreating(true);
    try {
      await api.post('/alerts', {
        saved_search_id: selectedSearch,
        frequency: frequency,
        schedule_time: scheduleTime,
      });
      handleCreateClose();
      fetchAlerts();
    } catch (err) {
      console.error('Error creating alert:', err);
      setError(err.response?.data?.detail || 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  // Toggle alert active status
  const handleToggle = async (alert) => {
    try {
      await api.post(`/alerts/${alert.id}/toggle`);
      fetchAlerts();
    } catch (err) {
      console.error('Error toggling alert:', err);
      setError('Failed to toggle alert');
    }
  };

  // Edit handlers
  const handleEditOpen = (alert) => {
    setEditAlert(alert);
    setEditFrequency(alert.frequency);
    setEditScheduleTime(alert.schedule_time || '09:00');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditAlert(null);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await api.put(`/alerts/${editAlert.id}`, {
        frequency: editFrequency,
        schedule_time: editScheduleTime,
      });
      handleEditClose();
      fetchAlerts();
    } catch (err) {
      console.error('Error updating alert:', err);
      setError('Failed to update alert');
    } finally {
      setEditSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteOpen = (alert) => {
    setDeleteAlert(alert);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteAlert(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/alerts/${deleteAlert.id}`);
      handleDeleteClose();
      fetchAlerts();
    } catch (err) {
      console.error('Error deleting alert:', err);
      setError('Failed to delete alert');
    } finally {
      setDeleting(false);
    }
  };

  // History handlers
  const handleHistoryOpen = async (alert) => {
    setHistoryAlert(alert);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const response = await api.get(`/alerts/${alert.id}/history`);
      setHistoryData(response.data.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryClose = () => {
    setHistoryOpen(false);
    setHistoryAlert(null);
    setHistoryData([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get available searches (not already alerting)
  const getAvailableSearches = () => {
    const alertedSearchIds = new Set(alerts.map(a => a.saved_search_id));
    return savedSearches.filter(s => !alertedSearchIds.has(s.id));
  };

  // Show login message if not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Email Alerts
          </Typography>
          <Typography color="text.secondary" paragraph>
            Log in to set up email alerts for your saved searches.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
          >
            Log In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Email Alerts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
        >
          Create Alert
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Email alerts require SMTP configuration. Contact your administrator to enable email delivery.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : alerts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No alerts yet
          </Typography>
          <Typography color="text.secondary" paragraph>
            Create an alert to get notified when new results match your saved searches.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOpen}
          >
            Create Your First Alert
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Search Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Last Sent</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow key={alert.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {alert.saved_search?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alert.saved_search?.query_text === '*' ? 'All' : alert.saved_search?.query_text}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SEARCH_TYPE_LABELS[alert.saved_search?.search_type] || alert.saved_search?.search_type}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {FREQUENCY_LABELS[alert.frequency]}
                    </Typography>
                    {alert.schedule_time && (
                      <Typography variant="body2" color="text.secondary">
                        at {alert.schedule_time}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(alert.last_sent_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={alert.is_active}
                      onChange={() => handleToggle(alert)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View History">
                      <IconButton
                        onClick={() => handleHistoryOpen(alert)}
                        size="small"
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleEditOpen(alert)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteOpen(alert)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Alert Dialog */}
      <Dialog open={createOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Email Alert</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Saved Search</InputLabel>
            <Select
              value={selectedSearch}
              label="Saved Search"
              onChange={(e) => setSelectedSearch(e.target.value)}
            >
              {getAvailableSearches().map((search) => (
                <MenuItem key={search.id} value={search.id}>
                  {search.name} ({SEARCH_TYPE_LABELS[search.search_type]})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {getAvailableSearches().length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              All your saved searches already have alerts. Create a new saved search first.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={frequency}
              label="Frequency"
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="immediate">Immediate</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>

          {frequency !== 'immediate' && (
            <FormControl fullWidth>
              <InputLabel>Time</InputLabel>
              <Select
                value={scheduleTime}
                label="Time"
                onChange={(e) => setScheduleTime(e.target.value)}
              >
                <MenuItem value="06:00">6:00 AM</MenuItem>
                <MenuItem value="09:00">9:00 AM</MenuItem>
                <MenuItem value="12:00">12:00 PM</MenuItem>
                <MenuItem value="18:00">6:00 PM</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !selectedSearch}
          >
            {creating ? 'Creating...' : 'Create Alert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Alert</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {editAlert?.saved_search?.name}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={editFrequency}
              label="Frequency"
              onChange={(e) => setEditFrequency(e.target.value)}
            >
              <MenuItem value="immediate">Immediate</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>

          {editFrequency !== 'immediate' && (
            <FormControl fullWidth>
              <InputLabel>Time</InputLabel>
              <Select
                value={editScheduleTime}
                label="Time"
                onChange={(e) => setEditScheduleTime(e.target.value)}
              >
                <MenuItem value="06:00">6:00 AM</MenuItem>
                <MenuItem value="09:00">9:00 AM</MenuItem>
                <MenuItem value="12:00">12:00 PM</MenuItem>
                <MenuItem value="18:00">6:00 PM</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editSaving}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Alert</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the alert for "{deleteAlert?.saved_search?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={handleHistoryClose} maxWidth="md" fullWidth>
        <DialogTitle>Alert History - {historyAlert?.saved_search?.name}</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No alerts have been sent yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Results</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{formatDate(entry.sent_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status}
                        size="small"
                        color={entry.status === 'sent' ? 'success' : entry.status === 'failed' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{entry.results_count}</TableCell>
                    <TableCell>{entry.error_message || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHistoryClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AlertsPage;
